import type { ApprovalAttributes } from '@/types/approval';

/** (구)매장 OPEN 품의 — 레거시 appr_edit_forms_shopopen */
export const STORE_OPEN_TYPE = 8;

export const isStoreOpenType = (apvType: number): boolean =>
  apvType === STORE_OPEN_TYPE;

/** agency=대리점(본문만, attributes 없음) / department=백화점 / direct=직영점 */
export type StoreOpenKind = 'agency' | 'department' | 'direct';

export type StoreOpenCandidate = {
  brandCode: string;
  brandName: string | null;
  season: string;
  departmentDocId: string;
  approvalDate: string;
  shopCode: string | null;
  shopName: string | null;
  shopTypeCode: string | null;
  constructionCompletedAt: string | null;
  interiorCompanyName: string | null;
  constructionType: string | null;
  isNoInterior: boolean;
  registeredAt: string | null;
};

export type StoreOpenInteriorLine = {
  category1Code: string | null;
  category1Name: string | null;
  category2Code: string | null;
  category2Name: string | null;
  category3Code: string | null;
  category3Name: string | null;
  itemName: string | null;
  spec: string | null;
  unit: string | null;
  areaInfo: string | null;
  unitPrice: string | null;
  quantity: string | null;
  amount: string | null;
  remarks: string | null;
};

export type StoreOpenInteriorDetail = {
  shopName: string | null;
  categoryAmounts: {
    categoryCode: string;
    categoryName: string | null;
    amount: string | null;
  }[];
  lines: StoreOpenInteriorLine[];
  rawTotal: string | null;
  rawTotalProfitApplyRate: string | null;
};

export type StoreOpenDraftSource = {
  code: number;
  /** 선택 키 미전달이면 서버가 빈 배열로 줌 */
  attributes: Record<string, unknown> | unknown[];
  display: {
    candidates: StoreOpenCandidate[];
    shopProfile: { area: string | null; workForm: string | null } | null;
    interiorEstimate: { totalThousandWon: string | null } | null;
    interiorDetail: StoreOpenInteriorDetail | null;
  };
};

/** 「등록」으로 불러온 매장 — 서버 값이라 화면에서 고치지 않음 */
export type StoreOpenStore = {
  brandCode: string;
  /** 작성은 후보 목록, 편집·조회는 저장값(PORTAL-15317 반영 전에는 null) */
  brandName: string | null;
  season: string;
  shopName: string;
  shopCode: string;
  departmentDocId: string;
  approvalDate: string;
  shopTypeCode: string;
  openDate: string;
  constructionType: string;
  interiorCompany: string;
  shopArea: string;
  shopForm: string;
  interiorCost: number | null;
  isNoInterior: boolean;
};

export const STORE_OPEN_INPUT_KEYS = [
  'ssSeasonGoalAmount',
  'ssMonthlyAvgAmount',
  'fwSeasonGoalAmount',
  'fwMonthlyAvgAmount',
  'totalMonthlyAvgAmount',
  'totalAnnualGoalAmount',
  'shopMasterName',
  'faCount',
  'lastWorkplace',
  'careerYears',
  'monthlyFee',
  'commissionType',
  'operationalState',
  'marginInfo1',
  'marginInfo2',
  'subBrandName1',
  'subBrandMargin1',
  'subBrandName2',
  'subBrandMargin2',
  'subBrandName3',
  'subBrandMargin3',
  'payState',
  'etcInfo',
  'shopEtcCost',
  'interiorNote',
  'rentContractPeriod',
  'rentDepositAmount',
  'rentMonthlyAmount',
  'rentMaintenanceFee',
  'rentPaymentDay',
  'rentEtcNote',
  'promoSms',
  'promoGift',
  'promoDiscount',
  'promoDoubleMileage',
  'promoForeignerEvent',
  'promoEtc',
  'coopSalesMgmt',
  'coopMgmtInfo',
  'coopMarketing',
  'headquartersSupport',
  'operationNote',
] as const;

export type StoreOpenInputKey = (typeof STORE_OPEN_INPUT_KEYS)[number];

export type StoreOpenInputs = Record<StoreOpenInputKey, string>;

/** 서버가 integer 로 받는 매출목표(천원) */
export const STORE_OPEN_INTEGER_KEYS: StoreOpenInputKey[] = [
  'ssSeasonGoalAmount',
  'ssMonthlyAvgAmount',
  'fwSeasonGoalAmount',
  'fwMonthlyAvgAmount',
  'totalMonthlyAvgAmount',
  'totalAnnualGoalAmount',
];

/** 레거시 type=number 칸 — 숫자만, 조회는 천 단위 콤마 */
export const STORE_OPEN_NUMBER_KEYS: StoreOpenInputKey[] = [
  ...STORE_OPEN_INTEGER_KEYS,
  'monthlyFee',
];

/** 레거시 fn_checkByte — 넘치면 안내 후 잘라냄 */
export const STORE_OPEN_BYTE_LIMITED_KEYS: StoreOpenInputKey[] = [
  'coopSalesMgmt',
  'coopMgmtInfo',
  'coopMarketing',
  'headquartersSupport',
  'operationNote',
];

export const STORE_OPEN_BYTE_LIMIT = 100;

export const STORE_OPEN_BYTE_LIMIT_MESSAGE =
  '메세지는 최대 100byte를 초과할 수 없습니다. 본문에 추가하여 입력하세요.';

/** 화면에 없는 저장값 — 레거시 문서 편집 시 지우지 않고 그대로 다시 보냄 */
const PASSTHROUGH_KEYS = [
  'subBrandName4',
  'subBrandMargin4',
  'goalRank',
  'commissionRate',
  'managerAnnualIncome',
  'shopLocation',
] as const;

type PassthroughKey = (typeof PASSTHROUGH_KEYS)[number];

export type StoreOpenFormState = {
  /** null = 양식 버튼을 누르지 않음(대리점과 같이 본문만 저장) */
  kind: StoreOpenKind | null;
  /** 편집 — 레거시처럼 양식·매장을 바꿀 수 없음 */
  locked: boolean;
  store: StoreOpenStore | null;
  inputs: StoreOpenInputs;
  passthrough: Partial<Record<PassthroughKey, string | null>>;
  /** 「등록」 응답 대기 중 — 화면의 매장이 곧 바뀌므로 그동안 저장·상신을 막음 */
  loading?: boolean;
};

export const createStoreOpenInputs = (): StoreOpenInputs =>
  Object.fromEntries(
    STORE_OPEN_INPUT_KEYS.map((key) => [key, '']),
  ) as StoreOpenInputs;

export const createStoreOpenState = (): StoreOpenFormState => ({
  kind: null,
  locked: false,
  store: null,
  inputs: createStoreOpenInputs(),
  passthrough: {},
});

/** 레거시는 매장 형태 C 일 때만 임대차·프로모션 칸을 보여줌(C1·C2 제외) */
export const isStoreOpenDirectShop = (store: StoreOpenStore | null): boolean =>
  store?.shopTypeCode === 'C';

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

const strOrNull = (v: unknown): string | null =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : null;

const intOrNull = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v) : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  return null;
};

export const formatStoreOpenNumber = (value: unknown): string => {
  const n = intOrNull(value);
  return n === null ? '' : n.toLocaleString('en-US');
};

const groupThousands = (digits: string) =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** 입력 중 표시 — 숫자만 남기고 천 단위 콤마. 소수 허용 칸은 앞의 - 와 소수점 아래를 그대로 둠(레거시 type=number) */
export const formatStoreOpenNumberInput = (
  value: string,
  allowDecimal = false,
): string => {
  const sign = allowDecimal && value.trim().startsWith('-') ? '-' : '';
  const [intRaw, ...rest] = value
    .replace(allowDecimal ? /[^\d.]/g : /[^\d]/g, '')
    .split('.');
  const int = intRaw.replace(/^0+(?=\d)/, '');
  if (rest.length === 0) return int === '' ? sign : sign + groupThousands(int);
  return `${sign}${groupThousands(int || '0')}.${rest.join('')}`;
};

/** 저장값 복원 — 평범한 숫자만 콤마를 넣고, 그 밖의 표기(1e3 등)는 그대로 둬 손대지 않은 재저장에도 값이 같게 */
const formatSavedDecimal = (value: string): string => {
  const match = /^(-?)(\d+)(\.\d+)?$/.exec(value.trim());
  return match
    ? `${match[1]}${groupThousands(match[2])}${match[3] ?? ''}`
    : value;
};

/** 월 예상 수수료는 서버가 문자열로 저장 — 소수를 반올림하면 손대지 않은 편집 저장에도 값이 바뀜 */
export const isStoreOpenDecimalKey = (key: StoreOpenInputKey) =>
  key === 'monthlyFee';

const toStoreOpenInput = (key: StoreOpenInputKey, value: unknown): string => {
  if (STORE_OPEN_INTEGER_KEYS.includes(key))
    return formatStoreOpenNumber(value);
  if (isStoreOpenDecimalKey(key)) return formatSavedDecimal(str(value));
  return str(value);
};

/** 레거시 fn_checkByte — escape 결과가 4자 넘는 글자(U+0100 이상)는 2byte */
export const countStoreOpenBytes = (value: string): number =>
  Array.from(value).reduce(
    (sum, ch) => sum + ((ch.codePointAt(0) ?? 0) > 0xff ? 2 : 1),
    0,
  );

export const trimStoreOpenBytes = (
  value: string,
  limit = STORE_OPEN_BYTE_LIMIT,
): string => {
  let bytes = 0;
  let out = '';
  for (const ch of Array.from(value)) {
    bytes += (ch.codePointAt(0) ?? 0) > 0xff ? 2 : 1;
    if (bytes > limit) break;
    out += ch;
  }
  return out;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** draft-source 프리필(attributes)+후보 → 화면에 고정으로 보일 매장 값 */
export const storeOpenStoreFromDraftSource = (
  source: StoreOpenDraftSource,
  candidate: StoreOpenCandidate,
): StoreOpenStore => {
  const a = isRecord(source.attributes) ? source.attributes : {};
  return {
    brandCode: str(a.brandCode) || candidate.brandCode,
    brandName: candidate.brandName,
    season: str(a.season) || candidate.season,
    shopName: str(a.shopName) || (candidate.shopName ?? ''),
    shopCode: str(a.shopCode) || (candidate.shopCode ?? ''),
    departmentDocId: str(a.departmentDocId) || candidate.departmentDocId,
    approvalDate: str(a.approvalDate) || candidate.approvalDate,
    shopTypeCode: str(a.shopTypeCode) || (candidate.shopTypeCode ?? ''),
    openDate: str(a.openDate) || (candidate.constructionCompletedAt ?? ''),
    constructionType:
      str(a.constructionType) || (candidate.constructionType ?? ''),
    interiorCompany:
      str(a.interiorCompany) || (candidate.interiorCompanyName ?? ''),
    shopArea: str(a.shopArea) || (source.display.shopProfile?.area ?? ''),
    shopForm: str(a.shopForm) || (source.display.shopProfile?.workForm ?? ''),
    interiorCost: intOrNull(a.interiorCost),
    isNoInterior:
      typeof a.isNoInterior === 'boolean'
        ? a.isNoInterior
        : candidate.isNoInterior,
  };
};

/** 프리필 중 입력칸 값(인테리어 미진행 비고 등) */
export const storeOpenInputsFromDraftSource = (
  source: StoreOpenDraftSource,
): StoreOpenInputs => {
  const a = isRecord(source.attributes) ? source.attributes : {};
  const inputs = createStoreOpenInputs();
  STORE_OPEN_INPUT_KEYS.forEach((key) => {
    const value = a[key];
    if (value === undefined || value === null) return;
    inputs[key] = toStoreOpenInput(key, value);
  });
  return inputs;
};

export type StoreOpenAttributes = StoreOpenStore & {
  values: Record<StoreOpenInputKey, string | number | null>;
  passthrough: Partial<Record<PassthroughKey, string | null>>;
};

/** 단건조회 attributes — 매장 키가 없으면(대리점·레거시 대리점 문서) null */
export const parseStoreOpenAttributes = (
  attributes: unknown,
): StoreOpenAttributes | null => {
  if (!isRecord(attributes)) return null;
  const a = attributes;
  if (!str(a.brandCode) || !str(a.departmentDocId)) return null;
  const values = Object.fromEntries(
    STORE_OPEN_INPUT_KEYS.map((key) => [
      key,
      STORE_OPEN_INTEGER_KEYS.includes(key)
        ? intOrNull(a[key])
        : strOrNull(a[key]),
    ]),
  ) as Record<StoreOpenInputKey, string | number | null>;
  const passthrough = Object.fromEntries(
    PASSTHROUGH_KEYS.map((key) => [key, strOrNull(a[key])]),
  );
  return {
    brandCode: str(a.brandCode),
    brandName: str(a.brandName) || null,
    season: str(a.season),
    shopName: str(a.shopName),
    shopCode: str(a.shopCode),
    departmentDocId: str(a.departmentDocId),
    approvalDate: str(a.approvalDate),
    shopTypeCode: str(a.shopTypeCode),
    openDate: str(a.openDate),
    constructionType: str(a.constructionType),
    interiorCompany: str(a.interiorCompany),
    shopArea: str(a.shopArea),
    shopForm: str(a.shopForm),
    interiorCost: intOrNull(a.interiorCost),
    isNoInterior: a.isNoInterior === true,
    values,
    passthrough,
  };
};

/** 편집 — 저장값이 없으면 대리점 문서(본문만) */
export const storeOpenAttributesToState = (
  saved: StoreOpenAttributes | null,
): StoreOpenFormState => {
  if (!saved)
    return { ...createStoreOpenState(), kind: 'agency', locked: true };
  const { values, passthrough, ...store } = saved;
  const inputs = createStoreOpenInputs();
  STORE_OPEN_INPUT_KEYS.forEach((key) => {
    const value = values[key];
    if (value === null) return;
    inputs[key] = toStoreOpenInput(key, value);
  });
  return {
    kind: isStoreOpenDirectShop(store) ? 'direct' : 'department',
    locked: true,
    store,
    inputs,
    passthrough,
  };
};

/** 양식 버튼을 눌렀는데 「등록」 전 — 임시저장·상신 모두 막음(레거시 동일) */
export const checkStoreOpenBeforeSave = (
  state: StoreOpenFormState | null | undefined,
): string | null => {
  if (!state) return null;
  if (state.loading) {
    return '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';
  }
  if (
    (state.kind === 'department' || state.kind === 'direct') &&
    !state.store
  ) {
    return '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';
  }
  return null;
};

const textOrNull = (v: string): string | null => {
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
};

/** 대리점·양식 미선택은 null — 키를 하나라도 보내면 서버가 백화점·직영점으로 보고 매장 키를 검사함 */
export const buildStoreOpenAttributes = (
  state: StoreOpenFormState | null | undefined,
): ApprovalAttributes | null => {
  const store = state?.store;
  if (!state || !store) return null;
  const values = Object.fromEntries(
    STORE_OPEN_INPUT_KEYS.map((key) => {
      const raw = state.inputs[key];
      if (STORE_OPEN_INTEGER_KEYS.includes(key)) return [key, intOrNull(raw)];
      if (key === 'monthlyFee') return [key, textOrNull(raw.replace(/,/g, ''))];
      return [key, textOrNull(raw)];
    }),
  );
  return {
    ...state.passthrough,
    ...values,
    brandCode: store.brandCode,
    season: store.season,
    shopName: store.shopName,
    shopCode: store.shopCode || null,
    departmentDocId: store.departmentDocId,
    approvalDate: store.approvalDate,
    shopTypeCode: store.shopTypeCode || null,
    openDate: store.openDate || null,
    constructionType: store.constructionType || null,
    interiorCompany: store.interiorCompany || null,
    shopArea: store.shopArea || null,
    shopForm: store.shopForm || null,
    interiorCost: store.interiorCost,
    isNoInterior: store.isNoInterior,
  };
};

/** 이탈 경고 비교값 — 같은 매장을 다시 「등록」해 서버 값만 바뀐 경우도 저장 대상이라 매장 전체를 비교 */
export const storeOpenKey = (state: StoreOpenFormState | null): string =>
  state && state.store ? JSON.stringify([state.store, state.inputs]) : '';

/** 상세 팝업 TOTAL(기업이윤 포함) — 레거시가 MLB Kids 신세계경기 건만 고정 값으로 보여줌 */
export const storeOpenDetailTotal = (
  store: Pick<StoreOpenStore, 'brandCode' | 'departmentDocId'>,
  totalThousandWon: unknown,
): string =>
  store.brandCode === 'I' && store.departmentDocId === '202307001'
    ? '96,275'
    : formatStoreOpenNumber(totalThousandWon);

/** 상세 팝업 금액 — 원 단위를 천 원으로(레거시 number_format(x/1000)) */
export const formatStoreOpenThousand = (value: string | null): string => {
  const n = Number(value ?? '');
  return Number.isFinite(n) && value !== null && value !== ''
    ? Math.round(n / 1000).toLocaleString('en-US')
    : '';
};
